import { FC, useEffect, useState, useMemo } from "react"
import { Column, useTable } from "react-table"
import ActivityService from "../../services/admin/activityService"
import HelperService from "../../services/helper"
import useNotyf from "../../context/useNotify"
import ReactPaginate from "react-paginate"
import { Meta, QueryParams } from "../../models/misc"
import { StyledInstructions, StyledPagination, StyledTable } from "./Styles"
import { Activity } from "../../models/activity"
import ReactModal from "../common/ReactModal"

const ActivityTable: FC = () => {
    const notyf = useNotyf()

    // Data
    const [activities, setActivities] = useState<Activity[]>([])
    const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null)
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false)

    const [meta, setMeta] = useState<Meta>({
        totalPages: 0,
        page: 1,
        pageSize: 25,
    })

    // API
    const fetchActivities = async (page: number = 1) => {
        try {
            const filterObj: QueryParams = {
                page: page,
                pageSize: meta.pageSize,
            }
            const data = await ActivityService.getActivities(filterObj)
            setActivities(data.data)
            setMeta(data.meta)
        } catch (err) {
            console.log("Error fetching activities: ", err)
            HelperService.showNotfyErr(notyf, err, "Error fetching activities.")
        }
    }

    // Methods
    const handleClickView = (activity: Activity) => {
        setSelectedActivity(activity)
        setShowDetailModal(true)
    }

    const closeModals = () => {
        setShowDetailModal(false)
        setSelectedActivity(null)
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString()
    }

    // Hooks
    useEffect(() => {
        fetchActivities()
    }, [])

    // Table Data
    const columns: Column<Activity>[] = useMemo(
        () => [
            {
                Header: "Date",
                accessor: "createdAt",
                Cell: ({ row }) => <span>{formatDate(row.original.createdAt)}</span>,
            },
            {
                Header: "User",
                accessor: "user",
                Cell: ({ row }) => <span>{row.original.user?.username || row.original.userId || "—"}</span>,
            },
            {
                Header: "Message",
                accessor: "message",
            },
            {
                Header: "Type",
                accessor: "modelType",
                Cell: ({ row }) => <span>{row.original.modelType || "—"}</span>,
            },
            {
                Header: "Actions",
                accessor: "_id",
                Cell: ({ row }) => (
                    <button className="btn btn-sm btn-primary" onClick={() => handleClickView(row.original)}>
                        View Details
                    </button>
                ),
            },
        ],
        [activities]
    )

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
        columns,
        data: activities,
    })

    return (
        <>
            <div className="d-flex flex-column mb-3">
                <StyledInstructions>
                    View system activity logs including stats grab jobs and other administrative actions.
                </StyledInstructions>
                <div className="table-responsive">
                    <StyledTable {...getTableProps()}>
                        <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                        {rows.map((row) => {
                            prepareRow(row)
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => {
                                        return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                    })}
                                </tr>
                            )
                        })}
                        </tbody>
                    </StyledTable>
                </div>
            </div>
            <div className="d-flex justify-content-end">
                {activities.length ? (
                    <StyledPagination>
                        <ReactPaginate
                            previousLabel={"Previous"}
                            nextLabel={"Next"}
                            pageCount={meta.totalPages}
                            onPageChange={({ selected }) => {
                                fetchActivities(selected + 1)
                            }}
                            containerClassName={"pagination"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            nextClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                        />
                    </StyledPagination>
                ) : null}
            </div>

            {/* Detail Modal */}
            <ReactModal
                modalOpen={showDetailModal}
                closeModal={() => closeModals()}
                component={
                    <>
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content bg-dark text-white">
                                <div className="modal-header bg-dark text-white">
                                    <h5 className="modal-title">Activity Details</h5>
                                </div>
                                <div className="modal-body">
                                    {selectedActivity && (
                                        <>
                                            <div className="mb-3">
                                                <strong>Date:</strong> {formatDate(selectedActivity.createdAt)}
                                            </div>
                                            <div className="mb-3">
                                                <strong>User:</strong> {selectedActivity.user?.username}
                                            </div>
                                            <div className="mb-3">
                                                <strong>Message:</strong> {selectedActivity.message}
                                            </div>
                                            <div className="mb-3">
                                                <strong>Model Type:</strong> {selectedActivity.modelType || "N/A"}
                                            </div>
                                            <div className="mb-3">
                                                <strong>Model ID:</strong> {selectedActivity.modelId || "N/A"}
                                            </div>
                                            {selectedActivity.meta && (
                                                <div className="mb-3">
                                                    <strong>Metadata:</strong>
                                                    <pre className="bg-secondary p-2 mt-2" style={{ maxHeight: "300px", overflow: "auto" }}>
                            {JSON.stringify(JSON.parse(selectedActivity.meta), null, 2)}
                          </pre>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer bg-dark text-white">
                                    <button type="button" className="btn btn-outline-light" onClick={() => closeModals()}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                }
            />
        </>
    )
}

export default ActivityTable
